import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useFilterStore } from '../../stores/filterStore';
import { useAuthStore } from '../../stores/authStore';
import AwsProdutosCatalog from './AwsProdutosCatalog';
import { fetchProductAnalysis, ProductAnalysisData } from './services/productsAPI';
import ModernDashboardHeader from '../../components/ModernDashboardHeader/ModernDashboardHeader';
import ProdutosPageSkeleton from './ProdutosPageSkeleton';
import styles from './ProdutosPage.module.css';
import ProductDetailModal from './components/Product/ProductDetailModal';
import { CarteiraSelector } from '../../components/CarteiraSelector/CarteiraSelector';
import CustomSelect from '../../components/CustomSelect';
import { Link } from 'react-router-dom';

// DateNavigator
import {
    DateNavigator,
    formatLocalDate,
    getYearRange,
    getMonthRange,
    getWeekRange,
} from '../../components/DateNavigator/DateNavigator';
import navStyles from '../../components/DateNavigator/DateNavigator.module.css';

// MUI Icons
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ShoppingBagOutlinedIcon from '@mui/icons-material/ShoppingBagOutlined';

/* ═══════════════════════════════════════════════
   HELPERS
   ═══════════════════════════════════════════════ */

const formatCurrency = (value: any) => {
    const numericValue = parseFloat(value);
    if (isNaN(numericValue)) return 'R$ 0,00';
    return numericValue.toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
};

const linhaStylesMap: { [key: string]: string } = {
    COMBAT: styles.linhaRedDark,
    PREMIUM: styles.linhaRed,
    'SAFE TECH': styles.linhaRed,
    SOFT: styles.linhaRed,
    KADESH: styles.linhaRed,

    IMBISEG: styles.linhaNavy,
    FLEX: styles.linhaNavy,
    'MICRO-K': styles.linhaNavy,
    'IMBISEG BI': styles.linhaNavy,
    'ACQUA BOOTS': styles.linhaNavyLight,
    IMPACTO: styles.linhaNavyLight,
    ADVENTURE: styles.linhaNavyLight,

    'NOBUCK FLEX': styles.linhaGray,
    'NOBUCK KAD': styles.linhaGray,
    'NOBUCK IMB': styles.linhaGray,
    ESTRADA: styles.linhaGray,
    FROSTY: styles.linhaGray,
    ADVANCE: styles.linhaGray,
};

const getStyleFromMap = (linha: string) => {
    const linhaNormalizada = linha?.trim().toUpperCase() || '';
    const specificClass = linhaStylesMap[linhaNormalizada] || '';
    return `${styles.linhaBase} ${specificClass}`.trim();
};

const ALL_LINE_OPTIONS = Object.keys(linhaStylesMap).sort();

const ITEMS_PER_PAGE = 15;

/* ═══════════════════════════════════════════════
   COMPONENT
   ═══════════════════════════════════════════════ */

// Cache em memória local para evitar Skeleton ao trocar de aba
let moduleCachedProductData: ProductAnalysisData[] | null = null;
let moduleCachedTotalItems: number = 0;

// Componente de Busca com Debounce Local para evitar renderizações a cada tecla e lag de digitação
const DebouncedSearchBar: React.FC<{
    value: string;
    onSearch: (value: string) => void;
    placeholder?: string;
}> = React.memo(({ value, onSearch, placeholder = 'Buscar por produto...' }) => {
    const [localValue, setLocalValue] = useState(value);

    useEffect(() => {
        setLocalValue(value);
    }, [value]);

    useEffect(() => {
        const handler = setTimeout(() => {
            onSearch(localValue);
        }, 400); // 400ms debounce
        return () => clearTimeout(handler);
    }, [localValue, onSearch]);

    return (
        <div className={styles.tableSearchBar}>
            <SearchIcon className={styles.tableSearchIcon} />
            <input
                type="text"
                placeholder={placeholder}
                value={localValue}
                onChange={(e) => setLocalValue(e.target.value)}
                className={styles.tableSearchInput}
            />
            {localValue && (
                <ClearIcon
                    className={styles.tableSearchClear}
                    onClick={() => setLocalValue('')}
                />
            )}
        </div>
    );
});

const ProdutosPageAnalytics: React.FC = () => {
    const { selectedCarteiraId } = useFilterStore();
    const isSupervisor = useAuthStore((state) => state.role === 'supervisor' || state.role === 'diretor');

    // ── Table data ──
    const [productData, setProductData] = useState<ProductAnalysisData[]>(moduleCachedProductData || []);
    const [isTableLoading, setIsTableLoading] = useState<boolean>(!moduleCachedProductData);
    const [cached, setCached] = useState<boolean>(false);
    const [cachedAt, setCachedAt] = useState<string | undefined>(undefined);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // ── Sorting & Pagination ──
    const [sortKey, setSortKey] = useState<keyof ProductAnalysisData>('FaturamentoTotal');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [totalItems, setTotalItems] = useState<number>(moduleCachedTotalItems);

    // ── Client filter ──
    const [selectedClient, setSelectedClient] = useState<{ name: string; id: number } | null>(null);

    // ── Search & Line filter ──
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedLine, setSelectedLine] = useState<string>('');

    // ── Modal ──
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [selectedProduct, setSelectedProduct] = useState<ProductAnalysisData | null>(null);

    // ── DateNavigator ──
    const [periodo, setPeriodo] = useState<'anual' | 'mensal' | 'semanal' | 'custom'>('anual');
    const [dataRef, setDataRef] = useState<string>(() => formatLocalDate(new Date()));
    const [dataInicio, setDataInicio] = useState<string>('');
    const [dataFim, setDataFim] = useState<string>('');

    const { startDate, endDate } = useMemo(() => {
        if (periodo === 'custom') {
            return { startDate: dataInicio, endDate: dataFim };
        }
        const dRef = dataRef || formatLocalDate(new Date());
        let range: { start: Date; end: Date };
        if (periodo === 'mensal') range = getMonthRange(dRef);
        else if (periodo === 'semanal') range = getWeekRange(dRef);
        else range = getYearRange(dRef);
        return {
            startDate: formatLocalDate(range.start),
            endDate: formatLocalDate(range.end),
        };
    }, [periodo, dataRef, dataInicio, dataFim]);

    const totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE);

    const handleSearchChange = useCallback((value: string) => {
        setSearchQuery(value);
        setCurrentPage(1);
    }, []);

    const handleRefresh = async () => {
        if (!startDate || !endDate) return;
        setIsRefreshing(true);
        try {
            const response = await fetchProductAnalysis({
                dataI: startDate,
                dataF: endDate,
                carteiraId: selectedCarteiraId,
                page: 1,
                limit: ITEMS_PER_PAGE,
                sortKey,
                sortOrder,
                filterProduto: searchQuery,
                filterLinha: selectedLine,
                clientId: selectedClient?.id,
                refresh: true
            });
            setProductData(response.data.productData || []);
            moduleCachedProductData = response.data.productData || [];
            setTotalItems(response.data.totalItems || 0);
            moduleCachedTotalItems = response.data.totalItems || 0;
            setCurrentPage(1);
            setCached(response.data.cached || false);
            setCachedAt(response.data.cachedAt);
        } catch (err: any) {
            setError(`Falha ao atualizar: ${err.message}`);
            console.error(err);
        } finally {
            setIsRefreshing(false);
        }
    };

    // ── Load table data ──
    const loadData = useCallback(async () => {
        if (!startDate || !endDate) return;
        setIsTableLoading(true);
        setError(null);
        try {
            const response = await fetchProductAnalysis({
                dataI: startDate,
                dataF: endDate,
                carteiraId: selectedCarteiraId,
                page: currentPage,
                limit: ITEMS_PER_PAGE,
                sortKey,
                sortOrder,
                filterProduto: searchQuery,
                filterLinha: selectedLine,
                clientId: selectedClient?.id,
            });
            setProductData(response.data.productData || []);
            moduleCachedProductData = response.data.productData || [];
            setTotalItems(response.data.totalItems || 0);
            moduleCachedTotalItems = response.data.totalItems || 0;
            setCached(response.data.cached || false);
            setCachedAt(response.data.cachedAt);
        } catch (err: any) {
            setError(`Falha ao carregar dados: ${err.message}`);
            console.error(err);
            setProductData([]);
            setTotalItems(0);
        } finally {
            setIsTableLoading(false);
        }
    }, [startDate, endDate, selectedCarteiraId, currentPage, sortKey, sortOrder, searchQuery, selectedLine, selectedClient]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    // ── Handlers ──

    const handleSort = (key: keyof ProductAnalysisData) => {
        if (sortKey === key) {
            setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortKey(key);
            setSortOrder('desc');
        }
        setCurrentPage(1);
    };

    const handleCardClick = (product: ProductAnalysisData) => {
        setSelectedProduct(product);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedProduct(null);
    };

    const handleClientSelect = (clientName: string, clientId: number) => {
        const newSelectedClient =
            selectedClient?.id === clientId
                ? null
                : { name: clientName, id: clientId };
        setSelectedClient(newSelectedClient);
        setCurrentPage(1);
        setSearchQuery('');
        setSelectedLine('');
    };

    const clearFilters = () => {
        setSearchQuery('');
        setSelectedLine('');
        setCurrentPage(1);
    };

    const hasActiveFilters = searchQuery !== '' || selectedLine !== '';

    // ── Sort icon helper ──
    const renderSortIcon = (key: keyof ProductAnalysisData) => {
        if (sortKey !== key) return null;
        return <span className={styles.sortIcon}>{sortOrder === 'asc' ? '▲' : '▼'}</span>;
    };

    // ── Pagination helpers (complaints-style) ──
    const handlePrevPage = () => {
        if (currentPage > 1) {
            setCurrentPage((p) => p - 1);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    const handleNextPage = () => {
        if (currentPage < totalPages) {
            setCurrentPage((p) => p + 1);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    /* ═══════════════════════════════════════════════
       RENDER
       ═══════════════════════════════════════════════ */

    return (
        <div className={styles.pageContainer}>
            {/* ── Client Pill ── */}
            {selectedClient && (
                <div className={styles.activeFilterPill}>
                    Filtrando por: <strong>{selectedClient.name}</strong>
                    <Link to={`/clientes/${selectedClient.id}`} className={styles.detailsLink}>
                        Ver Detalhes
                    </Link>
                    <button
                        onClick={() => handleClientSelect(selectedClient.name, selectedClient.id)}
                        className={styles.clearFilterBtn}
                    >
                        ×
                    </button>
                </div>
            )}

            {/* ── Glassmorphism Filter Bar ── */}
            <div className={styles.filterBar}>
                <div className={styles.filterTopRow}>
                    <div className={styles.filterGroup}>
                        <div className={navStyles.periodContainer}>
                            {(['anual', 'mensal', 'semanal', 'custom'] as const).map((p) => (
                                <button
                                    key={p}
                                    onClick={() => {
                                        setPeriodo(p);
                                        setCurrentPage(1);
                                    }}
                                    className={`${navStyles.periodButton} ${
                                        periodo === p ? navStyles.periodButtonActive : ''
                                    }`}
                                >
                                    {p === 'custom' ? 'Personalizado' : p}
                                </button>
                            ))}
                        </div>

                        <DateNavigator
                            label=""
                            periodo={periodo}
                            dataRef={dataRef}
                            dataInicio={dataInicio}
                            dataFim={dataFim}
                            onChange={(vals) => {
                                if (vals.periodo) setPeriodo(vals.periodo);
                                if (vals.dataRef) setDataRef(vals.dataRef);
                                if (vals.dataInicio) setDataInicio(vals.dataInicio);
                                if (vals.dataFim) setDataFim(vals.dataFim);
                                setCurrentPage(1);
                            }}
                        />
                    </div>
                    <ModernDashboardHeader 
                        variant="minimal" 
                        cacheInfo={{
                            cached: cached,
                            cachedAt: cachedAt,
                            onRefresh: handleRefresh,
                            isRefreshing: isRefreshing
                        }}
                    />
                </div>
                <div className={styles.kpiBottomRow}>
                    <span className={styles.kpiMicroItem}>
                        <ShoppingBagOutlinedIcon style={{ fontSize: 16, color: '#64748b' }} />
                        <span className={styles.kpiMicroLabel}>Total:</span>
                        <span className={styles.kpiMicroValue}>{totalItems.toLocaleString('pt-BR')} ref.</span>
                    </span>
                </div>
            </div>

            {error ? (
                <p style={{ padding: 24, color: '#dc2626' }}>{error}</p>
            ) : (
                <>
                    <div className={styles.tableContainer}>
                        <div className={styles.tableToolbar}>
                            <DebouncedSearchBar 
                                value={searchQuery} 
                                onSearch={handleSearchChange} 
                                placeholder="Buscar por produto..." 
                            />

                             <div className={styles.tableFilters}>
                                {isSupervisor && <CarteiraSelector />}
                                <CustomSelect
                                    id="linha-select"
                                    value={selectedLine}
                                    onChange={(e) => { setSelectedLine(e.target.value); setCurrentPage(1); }}
                                    placeholder="Linha"
                                    label="Linha"
                                    options={[{ value: '', label: 'Todas' }, ...ALL_LINE_OPTIONS.map((line) => ({ value: line, label: line }))]}
                                />
                                {hasActiveFilters && (
                                    <button className={styles.clearFiltersBtn} onClick={clearFilters}>
                                        Limpar Filtros
                                    </button>
                                )}
                            </div>
                        </div>
                        {isTableLoading ? (
                            <>
                                <table className={styles.table}>
                                    <thead>
                                        <tr>
                                            <th onClick={() => handleSort('Produto')}>
                                                Referência{renderSortIcon('Produto')}
                                            </th>
                                            <th>Descrição</th>
                                            <th onClick={() => handleSort('Linha')}>
                                                Linha{renderSortIcon('Linha')}
                                            </th>
                                            <th onClick={() => handleSort('FaturamentoTotal')}>
                                                Faturamento Total{renderSortIcon('FaturamentoTotal')}
                                            </th>
                                            <th onClick={() => handleSort('QuantidadeTotal')}>
                                                Qtd. Vendida{renderSortIcon('QuantidadeTotal')}
                                            </th>
                                            <th onClick={() => handleSort('MargemPercentual')}>
                                                Margem (%){renderSortIcon('MargemPercentual')}
                                            </th>
                                            <th onClick={() => handleSort('PrecoMedio')}>
                                                Preço Médio{renderSortIcon('PrecoMedio')}
                                            </th>
                                        </tr>
                                    </thead>
                                </table>
                                <ProdutosPageSkeleton />
                            </>
                        ) : (
                            <table className={styles.table}>
                                <thead>
                                    <tr>
                                        <th onClick={() => handleSort('Produto')}>
                                            Referência{renderSortIcon('Produto')}
                                        </th>
                                        <th>Descrição</th>
                                        <th onClick={() => handleSort('Linha')}>
                                            Linha{renderSortIcon('Linha')}
                                        </th>
                                        <th onClick={() => handleSort('FaturamentoTotal')}>
                                            Faturamento Total{renderSortIcon('FaturamentoTotal')}
                                        </th>
                                        <th onClick={() => handleSort('QuantidadeTotal')}>
                                            Qtd. Vendida{renderSortIcon('QuantidadeTotal')}
                                        </th>
                                        <th onClick={() => handleSort('MargemPercentual')}>
                                            Margem (%){renderSortIcon('MargemPercentual')}
                                        </th>
                                        <th onClick={() => handleSort('PrecoMedio')}>
                                            Preço Médio{renderSortIcon('PrecoMedio')}
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {productData.length === 0 ? (
                                        <tr>
                                            <td colSpan={7}>
                                                <div className={styles.emptyState}>
                                                    <ShoppingBagOutlinedIcon
                                                        style={{
                                                            fontSize: 48,
                                                            color: '#e2e8f0',
                                                            marginBottom: 16,
                                                        }}
                                                    />
                                                    <p>
                                                        {hasActiveFilters
                                                            ? 'Nenhum produto encontrado com os filtros atuais.'
                                                            : 'Nenhum produto encontrado.'}
                                                    </p>
                                                </div>
                                            </td>
                                        </tr>
                                    ) : (
                                        productData.map((product) => (
                                            <tr
                                                key={product.Produto}
                                                onClick={() => handleCardClick(product)}
                                                className={`${styles.clickableRow} ${getStyleFromMap(product.Linha)}`}
                                            >
                                                <td>
                                                    <span className={styles.productCell}>
                                                        {product.Produto}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span className={styles.lineCell}>
                                                        {product.Descricao || '-'}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span className={styles.lineCell}>
                                                        {product.Linha}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span className={styles.currencyCell}>
                                                        {formatCurrency(product.FaturamentoTotal)}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span className={styles.quantityCell}>
                                                        {product.QuantidadeTotal}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span
                                                        className={`${styles.marginCell} ${
                                                            product.MargemPercentual >= 0
                                                                ? styles.marginPositive
                                                                : styles.marginNegative
                                                        }`}
                                                    >
                                                        {product.MargemPercentual.toFixed(2)}%
                                                    </span>
                                                </td>
                                                <td>
                                                    <span className={styles.currencyCell}>
                                                        {formatCurrency(product.PrecoMedio)}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        )}

                        {/* ── Pagination ── */}
                        {totalPages > 1 && (
                            <div className={styles.pagination}>
                                <div className={styles.pageButtons}>
                                    <button
                                        className={styles.pageButton}
                                        onClick={handlePrevPage}
                                        disabled={currentPage === 1}
                                    >
                                        <ChevronLeftIcon fontSize="small" />
                                        Anterior
                                    </button>

                                    <div className={styles.pageNumbers}>
                                        {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                                            (page) => {
                                                if (
                                                    page === 1 ||
                                                    page === totalPages ||
                                                    (page >= currentPage - 2 &&
                                                        page <= currentPage + 2)
                                                ) {
                                                    return (
                                                        <button
                                                            key={page}
                                                            onClick={() => {
                                                                setCurrentPage(page);
                                                                window.scrollTo({
                                                                    top: 0,
                                                                    behavior: 'smooth',
                                                                });
                                                            }}
                                                            className={`${styles.pageNumber} ${
                                                                page === currentPage
                                                                    ? styles.pageNumberActive
                                                                    : ''
                                                            }`}
                                                        >
                                                            {page}
                                                        </button>
                                                    );
                                                }
                                                if (
                                                    page === currentPage - 3 ||
                                                    page === currentPage + 3
                                                ) {
                                                    return (
                                                        <span
                                                            key={page}
                                                            className={styles.pageEllipsis}
                                                        >
                                                            ...
                                                        </span>
                                                    );
                                                }
                                                return null;
                                            }
                                        )}
                                    </div>

                                    <button
                                        className={styles.pageButton}
                                        onClick={handleNextPage}
                                        disabled={currentPage === totalPages}
                                    >
                                        Próxima
                                        <ChevronRightIcon fontSize="small" />
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </>
            )}

            {/* ── Product Detail Modal ── */}
            {isModalOpen && selectedProduct && (
                <ProductDetailModal product={selectedProduct} onClose={handleCloseModal} startDate={startDate} endDate={endDate} />
            )}
        </div>
    );
};

/* ═══════════════════════════════════════════════
   ROOT EXPORT
   ═══════════════════════════════════════════════ */

const ProdutosPage: React.FC = () => {
    const { user } = useAuthStore();
    if (user?.isAws) {
        return <AwsProdutosCatalog />;
    }
    return <ProdutosPageAnalytics />;
};

export default ProdutosPage;
